import { formatName } from 'src/app/utils/name';
import { Component, inject, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MatTelInput, matTelInputValidator } from 'mat-tel-input';
import { GENDER_OPTIONS } from 'src/app/config/gender.constant';
import { getRoleLabel, USER_ROLES } from 'src/app/config/user-roles';
import { Country } from 'mat-tel-input/lib/country.model';
import { MaterialModule } from 'src/app/material.module';
import { Country as ICountry, CountrySelectComponent } from '@wlucha/ng-country-select';
import { FilePathPipe } from 'src/app/shared/pipes/file-path.pipe';
import { CommonModule } from '@angular/common';
import { ImageUploadModalComponent } from './image-upload-modal/image-upload-modal.component';
import { LanguagesService } from 'src/app/services/languages.service';
import { ActivitiesService } from 'src/app/services/activities.service';
import { User } from 'src/app/models';
import { SchoolInstructorService } from 'src/app/services/schoolInstructor.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    FormsModule, ReactiveFormsModule, MaterialModule,
    MatTelInput,
    CountrySelectComponent,
    FilePathPipe,
    CommonModule
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent {
  getRoleLabel = getRoleLabel;
  formatName = formatName;
  @ViewChild('countrySelect') countrySelect: any;

  authService = inject(AuthService);
  userService = inject(UserService);
  toaster = inject(ToastrService);
  route = inject(ActivatedRoute);
  dialog = inject(MatDialog);
  languageService = inject(LanguagesService);
  activityService = inject(ActivitiesService);
  schoolInstructorService = inject(SchoolInstructorService);
  loading = false;
  mySchools: any[] = [];
  USER_ROLES = USER_ROLES;

  activities$ = this.activityService.getActivities$();
  languages$ = this.languageService.getLanguages$();

  user: User | null = null;
  isMe = false;
  isInstructor = false;

  genderList = GENDER_OPTIONS;

  profileForm = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    secondName: new FormControl(''),
    thirdName: new FormControl(''),

    gender: new FormControl<number | null>(null),

    phone: new FormControl(null, [
      Validators.required,
      matTelInputValidator
    ]),
    phoneCountryIso: new FormControl<string | null>(null, [Validators.required]),
    nationality: new FormControl<any>('', [
      Validators.required,
      (control) => {
        const value = control.value;
        if (!value?.alpha2) return { required: true };
        return null;
      }
    ]),

    email: new FormControl('', [
      Validators.required,
      Validators.email
    ]),

    address: new FormControl(''),
    role: new FormControl({ value: null, disabled: true }),
    languageIds: new FormControl([]),
    activityIds: new FormControl([]),
  });

  get f() {
    return this.profileForm.controls;
  }

  ngOnInit() {
    const currentUser = this.authService.userSig();
    const paramUserId = this.route.snapshot.paramMap.get('userId');
    const isInstructor = this.authService.is(USER_ROLES.INSTRUCTOR);
    this.isInstructor = isInstructor;
    this.isMe = currentUser?.id == paramUserId;
    if(isInstructor){
      this.profileForm.get('languageIds')?.addValidators([Validators.required]);
      this.profileForm.get('activityIds')?.addValidators([Validators.required]);
      this.loadMySchools();
    }
    this.loadUser(paramUserId!);

  }

  loadMySchools() {
    this.schoolInstructorService.listSchools({ limit: -1, page: 1 }).subscribe({
      next: ({ rows }: any) => { this.mySchools = rows || []; },
      error: () => {},
    });
  }

  loadUser(id: string) {
    this.userService.getUser(id).subscribe({
      next: (user) => this.setUser(user),
      error: (err) => console.log(err)
    });
  }

  setUser(user: User) {
    this.user = user;
    const {activities = [], languages = []} = user as any;
    const countries = this.countrySelect.countries;
    const nationalityObj = countries.find((c: ICountry) => c.alpha2 === user.nationalityIso?.toLowerCase());
    this.profileForm.patchValue(({
      firstName: user.firstName,
      secondName: user.secondName,
      // thirdName: user.thirdName,
      email: user.email,
      gender: user.gender,
      phone: user.phone,
      phoneCountryIso: user.phoneCountryIso,
      nationality: nationalityObj,
      address: user.address,
      ...(this.isInstructor && {
        activityIds: activities.map((a: any)=>a.id),
        languageIds: languages.map((a: any)=>a.id)
      })
    } as any));
  }

  onPhoneCountryChange(country: Country) {
    this.f.phoneCountryIso.setValue(country?.iso2 || null);
  }

  submit() {
    if (!this.user?.id || this.profileForm.invalid) {
      this.toaster.error('Please fix errors before saving.');
      return;
    }


    const payload = {
      firstName: this.f.firstName.value,
      secondName: this.f.secondName.value,
      // thirdName: this.f.thirdName.value,
      gender: this.f.gender.value,
      // email: this.f.email.value,
      address: this.f.address.value,
      phone: this.f.phone.value,
      phoneCountryIso: this.f.phoneCountryIso.value?.toUpperCase(),
      nationalityIso: (this.f.nationality.value as any)?.alpha2,
      ...(this.isInstructor && {
        languageIds: this.f.languageIds.value,
        activityIds: this.f.activityIds.value,
      })
    };

    this.userService.updateUser(this.user.id, payload).subscribe({
      next: () => this.toaster.success('Profile updated successfully'),
      error: console.log
    });
  }

  editImage() {
    if (!this.user?.id) return;

    const dialogRef = this.dialog.open(ImageUploadModalComponent, {
      data: this.user,
    });

    dialogRef.afterClosed().subscribe((patch: Partial<User> | undefined) => {
      if (!patch) return;
      // Merge the diff onto the current rich user — the PATCH endpoint
      // returns a partial object, so we never want to replace wholesale.
      this.user = { ...(this.user as User), ...patch };
      if (this.isMe) {
        const current = this.authService.userSig();
        if (current) this.authService.setUserAndPermission({ ...current, ...patch });
      }
    });
  }

}
